/**
 * ConfirmDialog Component
 * Modal dialog for confirmation of destructive or important actions
 */

import React from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

export type ConfirmDialogVariant = "danger" | "warning" | "info";

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmDialogVariant;
  isLoading?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "warning",
  isLoading = false,
}) => {
  const variantStyles = {
    danger: {
      icon: "text-red-600",
      button: "bg-red-600 hover:bg-red-700 focus:ring-red-600",
    },
    warning: {
      icon: "text-yellow-600",
      button: "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-600",
    },
    info: {
      icon: "text-blue-600",
      button: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-600",
    },
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <div
      className={clsx(
        "fixed inset-0 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm",
        "z-[60]", // Higher than regular modals (z-50)
        { hidden: !isOpen },
      )}
      onClick={!isLoading ? onClose : undefined}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
              <ExclamationTriangleIcon
                className={clsx("w-6 h-6", variantStyles[variant].icon)}
              />
            </div>
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>

          {/* Message */}
          <p className="text-sm text-gray-600 mb-6">{message}</p>

          {/* Actions */}
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleConfirm}
              disabled={isLoading}
              className={clsx(
                "px-4 py-2 text-white rounded-md font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2",
                variantStyles[variant].button,
                {
                  "opacity-50 cursor-not-allowed": isLoading,
                },
              )}
            >
              {isLoading ? "Processing..." : confirmText}
            </button>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;

// Hook for easier usage
export const useConfirmDialog = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [config, setConfig] = React.useState<
    Omit<ConfirmDialogProps, "isOpen" | "onClose" | "onConfirm"> & {
      onConfirm?: () => void;
    }
  >({
    title: "",
    message: "",
  });
  const resolveRef = React.useRef<((value: boolean) => void) | null>(null);

  const confirm = React.useCallback(
    (options: Omit<ConfirmDialogProps, "isOpen" | "onClose" | "onConfirm">) => {
      return new Promise<boolean>((resolve) => {
        resolveRef.current = resolve;
        setConfig(options);
        setIsOpen(true);
      });
    },
    [],
  );

  const handleConfirm = React.useCallback(() => {
    if (resolveRef.current) {
      resolveRef.current(true);
      resolveRef.current = null;
    }
    setIsOpen(false);
  }, []);

  const handleClose = React.useCallback(() => {
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }
    setIsOpen(false);
  }, []);

  const ConfirmDialogComponent = React.useCallback(
    () => (
      <ConfirmDialog
        {...config}
        isOpen={isOpen}
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
    ),
    [isOpen, handleClose, handleConfirm, config],
  );

  return {
    confirm,
    ConfirmDialogComponent,
    isOpen,
    close: handleClose,
  };
};
